import { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EmailIcon from "@mui/icons-material/Email";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  Typography,
} from "@material-ui/core";

import backgroundImage from "assets/img/background/auth.jpeg";
import brandsImage from "assets/img/logos/group.png";
import componentStyles from "assets/theme/views/auth/login";
import componentStylesButtons from "assets/theme/components/button";
import themeColorsDesingSystem from "assets/theme/ds-colors";

import getEndpoints from "lib/apiEndpoints";
import MaskAndValid from "utils/maskAndValid";
import { IsMobile } from "utils/is-mobile";
import InputText from "components/_refactor/text/input";
import PasswordField from "components/_refactor/fields/password";
import ConfirmDialog from "components/_refactor/dialogs/confirm-dialog";

const useStyles = makeStyles(componentStyles);
const useStylesButtons = makeStyles(componentStylesButtons);

const usePageStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundImage: `linear-gradient(rgba(19, 41, 75, 0.88), rgba(9, 22, 41, 0.92)), url(${backgroundImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundAttachment: "fixed",
  },
  header: {
    padding: theme.spacing(3, 2, 0),
    textAlign: "center",
  },
  headerSubtitle: {
    color: "rgba(255, 255, 255, 0.75)",
    marginTop: theme.spacing(1),
    fontWeight: 400,
  },
  content: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4, 2),
  },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(16px)",
    borderRadius: 16,
    border: "1px solid rgba(255, 255, 255, 0.15)",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.35)",
    overflow: "visible",
    width: "100%",
  },
  cardHeader: {
    backgroundColor: "transparent",
    borderBottom: "none",
    paddingTop: theme.spacing(5),
    paddingBottom: 0,
  },
  cardContent: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(2, 4, 4),
    },
  },
  iconWrapper: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginTop: -36,
    border: "3px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
  },
  iconWrapperDanger: {
    backgroundColor: themeColorsDesingSystem.red.background,
    color: themeColorsDesingSystem.red.main,
  },
  iconWrapperSuccess: {
    backgroundColor: themeColorsDesingSystem.green.background,
    color: themeColorsDesingSystem.green.main,
  },
  description: {
    color: "rgba(255, 255, 255, 0.9)",
    lineHeight: 1.7,
    marginTop: theme.spacing(1),
  },
  warningBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2.5),
    padding: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: "rgba(255, 183, 77, 0.12)",
    border: "1px solid rgba(255, 183, 77, 0.35)",
  },
  warningIcon: {
    color: "#FFB74D",
    marginTop: 2,
    flexShrink: 0,
  },
  warningText: {
    color: "rgba(255, 255, 255, 0.85)",
    fontSize: "0.875rem",
    lineHeight: 1.6,
  },
  divider: {
    margin: theme.spacing(3, 0),
    backgroundColor: "rgba(255, 255, 255, 0.15)",
  },
  sectionLabel: {
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  errorAlert: {
    marginBottom: theme.spacing(2),
    borderRadius: 8,
  },
  successBox: {
    textAlign: "center",
    padding: theme.spacing(1, 0, 2),
  },
  successTitle: {
    color: themeColorsDesingSystem.white.main,
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  legalNote: {
    marginTop: theme.spacing(2.5),
    padding: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: "rgba(255, 255, 255, 0.06)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    color: "rgba(255, 255, 255, 0.65)",
    fontSize: "0.8125rem",
    lineHeight: 1.6,
  },
  footer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3, 2),
  },
  footerImage: {
    height: 48,
    width: "auto",
    opacity: 0.85,
  },
  deleteButton: {
    padding: theme.spacing(1.25, 4),
    borderRadius: 8,
    fontWeight: 600,
    letterSpacing: "0.02em",
    boxShadow: "0 4px 14px rgba(236, 74, 84, 0.35)",
  },
}));

async function signIn(login, password) {
  const endpoints = getEndpoints();
  const response = await fetch(`${endpoints.authDigitalApi}sign-in`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ login, password }),
  });

  const data = await response.json();

  if (!response.ok || !data?.Success) {
    throw new Error(
      data?.ErrorMessage || "E-mail ou senha inválidos. Tente novamente.",
    );
  }

  return data.Result;
}

async function deactivateAccount({ accessToken, idUser, senha, motivo }) {
  const endpoints = getEndpoints();
  const response = await fetch(
    `${endpoints.usersDigitalApi}me/deactivate-account`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        "K-IdUser": idUser,
      },
      body: JSON.stringify({ senha, motivo: motivo || undefined }),
    },
  );

  const data = await response.json();

  if (!response.ok || !data?.Success) {
    throw new Error(
      data?.ErrorMessage ||
        "Não foi possível excluir a conta. Tente novamente mais tarde.",
    );
  }

  return data.Result;
}

export default function ExclusaoConta() {
  const classes = {
    ...useStyles(),
    ...useStylesButtons(),
  };
  const pageClasses = usePageStyles();
  const isMobile = IsMobile();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [motivo, setMotivo] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [done, setDone] = useState(false);

  function validateForm() {
    return (
      email?.length > 0 &&
      MaskAndValid.validateEmail(email) &&
      password?.length > 0
    );
  }

  function handleSubmit(event) {
    event.preventDefault();
    setErrorMessage("");
    setConfirmOpen(true);
  }

  async function handleConfirm() {
    setLoading(true);
    setErrorMessage("");

    try {
      const signInResult = await signIn(email, password);

      await deactivateAccount({
        accessToken: signInResult.AccessToken,
        idUser: signInResult.IdUser,
        senha: password,
        motivo,
      });

      setConfirmOpen(false);
      setDone(true);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box className={pageClasses.page}>
      <Box className={pageClasses.content}>
        <Container maxWidth="sm">
          <Card classes={{ root: pageClasses.card }}>
            <Box
              className={`${pageClasses.iconWrapper} ${
                done
                  ? pageClasses.iconWrapperSuccess
                  : pageClasses.iconWrapperDanger
              }`}
            >
              {done ? (
                <CheckCircleOutlineIcon style={{ fontSize: 36 }} />
              ) : (
                <PersonOffIcon style={{ fontSize: 36 }} />
              )}
            </Box>

            {done ? (
              <CardContent classes={{ root: pageClasses.cardContent }}>
                <Box className={pageClasses.successBox}>
                  <Typography variant="h4" className={pageClasses.successTitle}>
                    Conta desativada
                  </Typography>
                  <Typography
                    variant="body1"
                    className={pageClasses.description}
                  >
                    Sua conta no App Karsten (konnect+) foi desativada com
                    sucesso. Seu acesso ao aplicativo foi encerrado.
                  </Typography>
                  <Box className={pageClasses.legalNote}>
                    Alguns dados poderão ser mantidos pelo período exigido por
                    lei (por exemplo, obrigações fiscais e contábeis), mesmo
                    após a desativação da conta.
                  </Box>
                </Box>
              </CardContent>
            ) : (
              <>
                <CardHeader
                  className={pageClasses.cardHeader}
                  title="Exclusão de conta"
                  titleTypographyProps={{
                    component: Typography,
                    variant: "h4",
                    align: "center",
                    style: {
                      color: themeColorsDesingSystem.white.main,
                      fontWeight: 600,
                    },
                  }}
                />

                <CardContent classes={{ root: pageClasses.cardContent }}>
                  <Typography
                    variant="body1"
                    align="center"
                    className={pageClasses.description}
                  >
                    Utilize este formulário para solicitar a exclusão da sua
                    conta e o encerramento do seu acesso ao App Karsten
                    (konnect+). Informe o e-mail e a senha utilizados no
                    aplicativo para confirmar sua identidade.
                  </Typography>

                  <Box className={pageClasses.warningBox}>
                    <WarningAmberIcon
                      className={pageClasses.warningIcon}
                      fontSize="small"
                    />
                    <Typography className={pageClasses.warningText}>
                      Esta ação é <strong>irreversível</strong> e disponível
                      apenas para contas de usuários externos do aplicativo.
                      Após a confirmação, você perderá o acesso imediatamente.
                    </Typography>
                  </Box>

                  <Divider className={pageClasses.divider} />

                  <Typography className={pageClasses.sectionLabel}>
                    Dados de acesso
                  </Typography>

                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <InputText
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Seu e-mail cadastrado"
                          startAdornment={<EmailIcon />}
                          white
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <PasswordField
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          white
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <InputText
                          label="Motivo (opcional)"
                          value={motivo}
                          onChange={(e) => setMotivo(e.target.value)}
                          placeholder="Conte-nos por que está excluindo sua conta"
                          multiline
                          rows={3}
                          white
                        />
                      </Grid>

                      {errorMessage && (
                        <Grid item xs={12}>
                          <Alert
                            severity="error"
                            className={pageClasses.errorAlert}
                          >
                            {errorMessage}
                          </Alert>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          justifyContent="center"
                          marginTop={1}
                        >
                          <Button
                            variant="contained"
                            type="submit"
                            disabled={!validateForm() || loading}
                            classes={{ root: classes.buttonContainedError }}
                            className={pageClasses.deleteButton}
                            style={
                              isMobile ? { width: "100%" } : { minWidth: 220 }
                            }
                          >
                            Excluir minha conta
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                </CardContent>
              </>
            )}
          </Card>
        </Container>
      </Box>

      <Box className={pageClasses.footer}>
        <img
          src={brandsImage}
          alt="Karsten"
          className={pageClasses.footerImage}
        />
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirmar exclusão de conta"
        message="Tem certeza de que deseja excluir sua conta? Essa ação não pode ser desfeita e você perderá o acesso ao aplicativo imediatamente."
        confirmLabel="Excluir conta"
        cancelLabel="Cancelar"
        confirmColor="secondary"
        loading={loading}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
